// src/components/DashboardTabs.jsx
import React, { useId, useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./DashboardTabs.css";
import ChartNavigationBar from "./ChartNavigationBar";
import PeriodFilter from "./PeriodFilter";
import RequestReportButton from "./RequestReportButton";
import BrowseDiningSessions from "./BrowseDiningSessions";

const MOBILE_BREAKPOINT = 1023;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handle = () => setIsMobile(mql.matches);
    mql.addEventListener("change", handle);
    return () => mql.removeEventListener("change", handle);
  }, []);
  return isMobile;
}

export default function DashboardTabs({
  items,
  initialKey = items?.[0]?.key, // fallback if no route param
  RestaurantID,
  browseDiningSessions,
}) {
  const navigate = useNavigate();
  const { categoryID, chartID } = useParams();
  const location = useLocation();
  const tablistRef = useRef(null);
  const baseId = useId();
  const isMobile = useIsMobile();

  console.log("[BROWSE DINING SESSIONS] " + browseDiningSessions);

  // 🔽 collapse / expand ChartNavigationBar
  const [navCollapsed, setNavCollapsed] = useState(false);
  // Accordion: which section is expanded on mobile (null = all collapsed)
  const [expandedAccordionKey, setExpandedAccordionKey] = useState(null);

  // --- Helper: get category from URL (for first load only) ---
  const getRouteCategoryId = () => {
    // 1) If the route defines :categoryID as a param, just use it
    if (categoryID != null) return String(categoryID);

    const pathname = location.pathname || "";
    const segments = pathname.split("/").filter(Boolean);

    // 2) Special case: /data/browse/dining/:page -> force to category "6"
    if (
      segments[0] === "data" &&
      segments[1] === "browse" &&
      segments[2] === "dining"
    ) {
      // This is your "Browse Dining Sessions" category (dataCategoryID = 6)
      return "6";
    }

    // 3) Generic case for /data/... routes:
    //    Take the FIRST numeric segment after "data" as the category
    if (segments[0] === "data") {
      const numericSeg = segments
        .slice(1) // skip "data"
        .find((s) => /^\d+$/.test(s));
      if (numericSeg) return String(numericSeg);
    }

    return null;
  };

  // 🧠 Remember the route category as it was on first mount
  const initialRouteCategoryIdRef = useRef(getRouteCategoryId());

  // 🔐 active tab key (string)
  const [active, setActive] = useState("");

  // Track if we've already initialized active from route/items
  const firstLoadRef = useRef(true);

  // 🧠 Helper to find the item that corresponds to a given route category
  const findItemForRouteCategory = (rcid) => {
    if (!items || !rcid) return null;

    // 1) Try match tab key directly
    let match =
      items.find((i) => String(i.key) === String(rcid)) ?? null;
    if (match) return match;

    // 2) Otherwise, try match by defaultChartCategory
    match =
      items.find(
        (i) =>
          i.defaultChartCategory != null &&
          String(i.defaultChartCategory) === String(rcid)
      ) ?? null;

    return match;
  };

  // Common logic used for both:
  //  - initial URL-based selection
  //  - actual user clicks
  const selectTab = (item, { syncRoute = true } = {}) => {
    if (!item) return;

    const keyStr = String(item.key);
    setActive(keyStr);
    firstLoadRef.current = false; // once we do this, we will never init from URL again

    if (item.defaultChartCategory) {
      const nav = `/data/${item.defaultChartCategory}/${item.defaultChartId}`;

      // Set chartType for the first chart in that tab
      try {
        const chartType =
          items[item.defaultChartCategory - 1]?.buttonsList?.[
            item.defaultChartId - 1
          ]?.chartType;

        if (chartType) {
          localStorage.setItem("chartType", chartType);
          console.log(
            "Chart Type",
            items[item.defaultChartCategory - 1].label,
            "-",
            chartType
          );
        }
      } catch (e) {
        console.warn("Could not set chartType from items", e);
      }

      if (syncRoute) {
        console.log("navigate to", nav);
        navigate(nav);
      }
    }
  };

  // ✅ Initialize active ONCE, when items are available,
  // and "activate" the tab as if it were clicked (but no extra navigate)
  useEffect(() => {
    if (!items || items.length === 0) return;
    if (!firstLoadRef.current) return; // already initialized

    const rcid = initialRouteCategoryIdRef.current;

    let initialItem = null;

    if (rcid) {
      // Try to find the tab whose key OR defaultChartCategory matches the route category
      initialItem = findItemForRouteCategory(rcid);
    }

    if (!initialItem) {
      // Fallbacks if route category doesn't match anything
      const resolvedInitialKey =
        initialKey != null ? String(initialKey) : items[0]?.key;

      initialItem =
        items.find((i) => String(i.key) === String(resolvedInitialKey)) ??
        items[0];
    }

    console.log(
      "[DashboardTabs] Initial route category:",
      rcid,
      " -> initialItem.key:",
      initialItem?.key,
      "defaultChartCategory:",
      initialItem?.defaultChartCategory
    );

    // Act like we clicked this tab, but don't change the route again
    selectTab(initialItem, { syncRoute: false });
    setExpandedAccordionKey(String(initialItem?.key ?? ""));
  }, [items, initialKey]);

  useEffect(() => {
    if (isMobile && active) setExpandedAccordionKey(active);
  }, [isMobile]);

  // 🔁 date/period selection from PeriodFilter
  const [periodSelection, setPeriodSelection] = useState(null);

  const periodFilter = [
    { title: "Today", value: "today" },
    { title: "This Week", value: "this_week" },
    { title: "This Month", value: "this_month" },
    { title: "This Year", value: "this_year" },
    { title: "Custom Range", value: "custom_range" },
  ];

  const handleTabClick = (item) => {
    // Real user click: select tab + update URL
    selectTab(item, { syncRoute: true });
  };

  const handleAccordionHeaderClick = (item) => {
    const keyStr = String(item.key);
    if (keyStr === expandedAccordionKey) {
      setExpandedAccordionKey(null);
    } else {
      setExpandedAccordionKey(keyStr);
      selectTab(item, { syncRoute: true });
    }
  };

  const handleFilterChange = ({ mode, start, end }) => {
    console.log("Filter 1:", mode, start, end);
    setPeriodSelection({ mode, start, end });
  };

  // 🔍 Find active item by comparing keys as strings
  const activeItem =
    items?.find((i) => String(i.key) === String(active)) ?? items?.[0];

  const routeCategoryId = initialRouteCategoryIdRef.current;

  console.log("Active tab key:", active);
  console.log("Route categoryID (param):", categoryID, "chartID:", chartID);
  console.log("Route-derived categoryID (initial):", routeCategoryId);
  console.log("Active item:", activeItem?.label, "key:", activeItem?.key);
  console.log(
    "Active item defaultChartCategory:",
    activeItem?.defaultChartCategory
  );
  console.log("Period selection:", periodSelection);

  if (!activeItem) return null; // safety

  if (isMobile) {
    return (
      <div className="tm-tabs tm-tabs--accordion">
        <div className="tm-data-toolbar">
          <PeriodFilter filter={periodFilter} onChange={handleFilterChange} />
        </div>
        <div className="tm-accordion" role="tablist" aria-label="Dashboard sections">
          {items.map((item) => {
            const keyStr = String(item.key);
            const selected = keyStr === String(active);
            const expanded = keyStr === expandedAccordionKey;
            const tabId = `${baseId}-tab-${keyStr}`;
            const panelId = `${baseId}-panel-${keyStr}`;
            return (
              <div key={keyStr} className="tm-accordion-item" data-key={keyStr}>
                <button
                  id={tabId}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  tabIndex={expanded ? 0 : -1}
                  className={`tm-accordion-header ${expanded ? "is-active" : ""}`}
                  onClick={() => handleAccordionHeaderClick(item)}
                >
                  <span className="tm-accordion-label">{item.label}</span>
                  <span className="tm-accordion-chevron" aria-hidden>{expanded ? "▼" : "▶"}</span>
                </button>
                <div
                  id={panelId}
                  role="tabpanel"
                  aria-labelledby={tabId}
                  className="tm-accordion-panel"
                  hidden={!expanded}
                >
                  <div className="tm-data-toolbar tm-data-toolbar--compact">
                    <div className="tm-data-toolbar-actions">
                      <RequestReportButton RestaurantID={RestaurantID} />
                      <button
                        type="button"
                        className="tm-chartnav-toggle-btn"
                        onClick={() => setNavCollapsed((v) => !v)}
                      >
                        {navCollapsed
                          ? "Show Chart Navigation"
                          : "Hide Chart Navigation"}
                      </button>
                    </div>
                  </div>
                  {!navCollapsed && (
                    <ChartNavigationBar
                      key={item.key}
                      buttonsList={item.buttonsList}
                      urlPrefix={item.urlPrefix}
                    />
                  )}
                  <div className="tm-panel">
                    {browseDiningSessions === true ? (
                      <BrowseDiningSessions periodSelection={periodSelection} />
                    ) : selected ? (
                      React.cloneElement(item.element, {
                        title: item.title,
                        periodSelection,
                      })
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="tm-tabs">
      <div
        className="tm-tablist row"
        role="tablist"
        aria-label="Dashboard sections"
        ref={tablistRef}
      >
        {items.map((item) => {
          const keyStr = String(item.key);
          const selected = keyStr === String(active);
          const tabId = `${baseId}-tab-${keyStr}`;
          const panelId = `${baseId}-panel-${keyStr}`;
          return (
            <button
              key={keyStr}
              data-key={keyStr}
              id={tabId}
              role="tab"
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              className={`tm-tab col-md-2 ${selected ? "is-active" : ""}`}
              onClick={() => handleTabClick(item)}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="tm-data-toolbar">
        <div className="tm-data-toolbar-actions">
          <RequestReportButton RestaurantID={RestaurantID} />
          <button
            type="button"
            className="tm-chartnav-toggle-btn"
            onClick={() => setNavCollapsed((v) => !v)}
          >
            {navCollapsed ? "Show Chart Navigation" : "Hide Chart Navigation"}
          </button>
        </div>
        <PeriodFilter filter={periodFilter} onChange={handleFilterChange} />
      </div>

      {!navCollapsed && (
        <ChartNavigationBar
          key={activeItem.key}
          buttonsList={activeItem.buttonsList}
          urlPrefix={activeItem.urlPrefix}
        />
      )}

      <div
        id={`${baseId}-panel-${activeItem.key}`}
        role="tabpanel"
        aria-labelledby={`${baseId}-tab-${activeItem.key}`}
        className="tm-panel"
      >
        {browseDiningSessions === true ? (
          <BrowseDiningSessions periodSelection={periodSelection} />
        ) : (
          React.cloneElement(activeItem.element, {
            title: activeItem.title,
            periodSelection,
          })
        )}
      </div>
    </div>
  );
}
